import { createContext, Dispatch, SetStateAction } from "react";
import { z } from "zod";

import { ExpandedJobProps, JobFormData } from "@/lib/types";
import { ScreeningQuestionSchema } from "@/lib/zod/schemas/job";
import { JobStep } from "./use-job-state-machine";

export type ScreeningQuestion = z.infer<typeof ScreeningQuestionSchema>;

export type JobBuilderProps = {
  showJobBuilder: boolean;
  setShowJobBuilder: Dispatch<SetStateAction<boolean>>;
  props?: ExpandedJobProps;
  duplicateProps?: ExpandedJobProps;
  homepageDemo?: boolean;
};

export interface JobModalContextType {
  workspaceId?: string;
  workspacePlan?: string;
  workspaceNextPlan?: any;
  workspaceLogo?: string;
  generatingMetatags: boolean;
  currentStep: JobStep;
  setCurrentStep: (step: JobStep) => void;
  savedJobId?: string;
  jobData?: Partial<JobFormData> | null;
  loadingJobData?: boolean;
  saveJobData: (data: Partial<JobFormData>) => Promise<any>;
  stateMachine: {
    currentStep: JobStep;
    previousStep: JobStep | null;
    isSaving: boolean;
    isValidating: boolean;
    hasJobId: boolean;
    hasSkills: boolean;
    hasQuiz: boolean;
    lastSaved: Record<JobStep, Date | null>;
    canNavigateToStep: (step: JobStep) => boolean;
    validateStep: (step: JobStep) => Promise<boolean>;
    saveStep: (step: JobStep, force?: boolean) => Promise<boolean>;
    navigateToStep: (step: JobStep, forceSave?: boolean) => Promise<boolean>;
    nextStep: () => Promise<boolean>;
    prevStep: () => Promise<boolean>;
    trackEvent: (eventName: string, properties?: Record<string, any>) => void;
  };
}

// Create the context with default values
export const JobModalContext = createContext<JobModalContextType>({
  generatingMetatags: false,
  currentStep: "basic",
  setCurrentStep: () => { },
  savedJobId: "",
  jobData: {},
  loadingJobData: false,
  saveJobData: async () => {
    console.warn("saveJobData called before being properly initialized");
    return {};
  },
  stateMachine: {
    currentStep: "basic",
    previousStep: null,
    isSaving: false,
    isValidating: false,
    hasJobId: false,
    hasSkills: false,
    hasQuiz: false,
    lastSaved: {
      basic: null,
      skills: null,
      quiz: null,
      customize: null,
      share: null,
      insights: null,
    },
    canNavigateToStep: () => false,
    validateStep: async () => false,
    saveStep: async () => false,
    navigateToStep: async () => false,
    nextStep: async () => false,
    prevStep: async () => false,
    trackEvent: () => { },
  },
});

export interface StepFunctions {
  saveChanges: () => Promise<boolean>;
}

export interface JobStepsContextType {
  registerStepFunctions: (stepId: string, functions: StepFunctions) => void;
  unregisterStepFunctions: (stepId: string) => void;
  setShowJobBuilder: (show: boolean) => void;
  saveCurrentStep: () => Promise<boolean>;
  currentStep: string;
  setCurrentStep: (stepId: string) => void;
}

export const JobStepsContext = createContext<JobStepsContextType>({
  currentStep: "basic",
  saveCurrentStep: async () => {
    return true;
  },
  setShowJobBuilder: (show: boolean) => { },
  setCurrentStep: async (stepId: string) => {
    return true;
  },
  registerStepFunctions: (stepId: string, functions: StepFunctions) => void {},
  unregisterStepFunctions: (stepId: string) => void {},
});

// Types and constants are the same as before...
export type JobShareProps = {
  jobId: string;
};

export type Candidate = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  custom1?: string;
  custom2?: string;
};

export type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
};

export interface SkillProgress {
  skill: string;
  status: "pending" | "processing" | "completed" | "error";
  progress: number;
  message: string;
}
